/**
 * Speicherung der Leadsheet-Metadaten (Titel, Tempo) im Browser.
 *
 * Eigener Schlüssel, unabhängig vom Pattern-Speicher aus DP-1. Defekte
 * oder fremde Daten führen zu sicheren Rückfallwerten statt zu Fehlern.
 */

import {
  DEFAULT_META,
  deserializeMeta,
  serializeMeta,
  type LeadsheetMeta,
} from '../model/leadsheet';
import type { PatternStore } from './patternStorage';

export const META_STORAGE_KEY = 'drumpattern-editor:leadsheet-meta:v1';

function resolveStore(store?: PatternStore): PatternStore | null {
  if (store) {
    return store;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage;
    }
  } catch {
    // Zugriff auf localStorage kann in manchen Umgebungen scheitern.
  }
  return null;
}

/** Speichert die Metadaten. Fehler werden als `false` gemeldet. */
export function saveMeta(meta: LeadsheetMeta, store?: PatternStore): boolean {
  const target = resolveStore(store);
  if (!target) {
    return false;
  }
  try {
    target.setItem(META_STORAGE_KEY, JSON.stringify(serializeMeta(meta)));
    return true;
  } catch {
    return false;
  }
}

/**
 * Lädt die Metadaten. Liefert die Standardwerte, wenn nichts gespeichert
 * ist oder die Daten ungültig bzw. beschädigt sind.
 */
export function loadMeta(store?: PatternStore): LeadsheetMeta {
  const target = resolveStore(store);
  if (!target) {
    return DEFAULT_META;
  }

  let raw: string | null;
  try {
    raw = target.getItem(META_STORAGE_KEY);
  } catch {
    return DEFAULT_META;
  }
  if (raw === null) {
    return DEFAULT_META;
  }

  try {
    return deserializeMeta(JSON.parse(raw));
  } catch {
    return DEFAULT_META;
  }
}

/** Entfernt die gespeicherten Metadaten. */
export function clearStoredMeta(store?: PatternStore): void {
  const target = resolveStore(store);
  if (!target) {
    return;
  }
  try {
    target.removeItem(META_STORAGE_KEY);
  } catch {
    // Fehler beim Löschen werden ignoriert.
  }
}
